// Quản lý phiên đăng nhập + refresh token.
// Single-flight bằng promise (thay cho isRefreshing bool + failedQueue thủ công),
// refresh call đi qua httpBare (không interceptor) để chống deadlock self-queue.
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import ROUTER from '@/shared/config/routes'

import {
  deleteAuthToken,
  deleteRefreshToken,
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '@/shared/lib/storage'
import queryClient from '@/shared/queries/queryClient'
import AuthService from '@/shared/services/AuthService'
import { sessionExpiredStore } from './sessionExpired'

// Nơi lưu token do STORAGE.REMEMBER_HOME (đặt lúc login) quyết định — setAuthToken/
// setRefreshToken tự đọc flag đó, không đoán lại theo nơi token đang nằm nữa.
const saveTokens = (accessToken: string, refreshToken?: string): void => {
  setAuthToken(accessToken)
  if (refreshToken) {
    setRefreshToken(refreshToken)
  }
}

const clearAllTokens = (): void => {
  deleteAuthToken()
  deleteRefreshToken()
}

// Kết thúc hẳn phiên: xoá token + cache rồi về trang chủ (login). Gọi từ nút "Đăng nhập lại".
export const endSessionAndGoHome = (): void => {
  clearAllTokens()
  queryClient.clear()
  sessionExpiredStore.hide()
  window.location.href = ROUTER.HOME
}

// Phiên đã hết: chỉ phát tín hiệu mở modal (không chuyển trang, để modal còn hiển thị).
// Không hiện modal khi chưa có phiên: đang ở màn đăng nhập thật (ROUTER.LOGIN) hoặc không còn
// access token. KHÔNG dùng ROUTER.HOME để quyết — '/' là trang đích sau đăng nhập, user đã
// đăng nhập vẫn đứng ở đó và phải thấy modal.
export const logoutSession = (): void => {
  const noSession = window.location.pathname === ROUTER.LOGIN || !getAuthToken()
  if (noSession) {
    endSessionAndGoHome()
    return
  }
  sessionExpiredStore.show()
}

// ---- Single-flight refresh ----
let refreshPromise: Promise<string> | null = null

const doRefresh = async (): Promise<string> => {
  const refreshToken = getRefreshToken()

  try {
    const response = await AuthService.refreshToken(refreshToken ?? undefined)
    if (!response.isError && response.object?.accessToken) {
      saveTokens(response.object.accessToken, response.object.refreshToken)
      return response.object.accessToken
    }
  } catch {
    // rơi xuống logoutSession bên dưới
  }

  logoutSession()
  throw new Error('Refresh token thất bại')
}

// Nếu đang refresh thì trả về CÙNG promise (dedupe concurrent 401),
// ngược lại khởi tạo refresh mới và tự reset sau khi settle.
export const ensureValidToken = (): Promise<string> => {
  // Modal "phiên hết hạn" đang mở: token chưa bị xoá nên đừng gọi refresh thêm (chỉ thất bại lặp).
  if (sessionExpiredStore.getSnapshot()) {
    return Promise.reject(new Error('Phiên đăng nhập đã hết hạn'))
  }
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// Retry request sau khi có token mới. Gọi từ response interceptor khi gặp 401.
export const retryWithFreshToken = async (
  config: InternalAxiosRequestConfig & { _retry?: boolean; _skipAuth?: boolean },
  instance: AxiosInstance
): Promise<AxiosResponse> => {
  const token = await ensureValidToken()
  config._retry = true
  config.headers.Authorization = `Bearer ${token}`
  return instance(config)
}

// Normalize auth error: HTTP 401 hoặc envelope { statusCode: 401 }.
export const isAuthError = (status: number, data?: any): boolean => {
  if (status === 401) return true
  const envelopeStatus = Number(data?.statusCode ?? data?.StatusCode)
  return envelopeStatus === 401
}
